import { z } from 'zod/v4';

import { PERFORMANCE_STATUS_SNAKE_CASE_MAP } from './components/diagnostics/performanceMonitoring/constants';

import { CommonSearchParamsSchema } from '@/shared/utils/schemas';

export const pacingMetricsStatuses = [
  'Learning',
  'Testing',
  'Constrained',
  'Optimized',
  'N/A',
] as const;
export type PacingMetricStatus = (typeof pacingMetricsStatuses)[number];

export const efficiencyCategories = [
  'ad_format',
  'browser',
  'device_make',
  'device_type',
  'os_name',
  'region',
  'supply_vendor',
  'site',
] as const;
export type EfficiencyCategory = (typeof efficiencyCategories)[number];

export const metaEfficiencyCategories = [
  'gender',
  'device',
  'age',
  'region',
  'platform',
  'placement',
] as const;
export type MetaEfficiencyCategory = (typeof metaEfficiencyCategories)[number];

export type FilterDimension = EfficiencyCategory | MetaEfficiencyCategory;

export const StarvationBidderSearchParamsSchema = z.object({
  starve: z.enum(['diag', 'iex', 'how', 'plan']).catch('diag'), // starvation bidder tab
  diag: z
    .enum(['detailedDiagnostics', 'performanceMonitoring'])
    .catch('performanceMonitoring'), // diagnostics sub-tab
  performanceStatus: z
    .enum(Object.values(PERFORMANCE_STATUS_SNAKE_CASE_MAP))
    .default(PERFORMANCE_STATUS_SNAKE_CASE_MAP.starvationBidderApplied)
    .catch(PERFORMANCE_STATUS_SNAKE_CASE_MAP.starvationBidderApplied),
  ...CommonSearchParamsSchema.shape,
  ordering: z
    .enum([
      'campaign_name',
      '-campaign_name',
      'yesterday_spend',
      '-yesterday_spend',
      'total_spend',
      '-total_spend',
      'needed_spend_yesterday',
      '-needed_spend_yesterday',
      'budget',
      '-budget',
    ])
    .optional()
    .catch(undefined),
  budgetMin: z.coerce.number().min(0).optional().catch(undefined),
  budgetMax: z.coerce.number().min(0).optional().catch(undefined),
  algorithmStatus: z
    .array(z.enum(pacingMetricsStatuses))
    .max(5)
    .refine((arr) => new Set(arr).size === arr.length)
    .optional()
    .catch(undefined),
  efficiencyCategory: z
    .union([z.enum(efficiencyCategories), z.enum(metaEfficiencyCategories)])
    .default('site')
    .catch('site'),
  adGroup: z.string().optional().catch(undefined),
  effPage: CommonSearchParamsSchema.shape.page,
  effPageSize: CommonSearchParamsSchema.shape.pageSize,
  efficiencyInsightsOrdering: z
    .enum([
      'ranking',
      '-ranking',
      'cpm',
      '-cpm',
      'cpv',
      '-cpv',
      'category_name',
      '-category_name',
      'efficiency_index',
      '-efficiency_index',
    ])
    .optional()
    .catch(undefined),
  campaignStartDate: z.iso.date().optional().catch(undefined),
  campaignEndDate: z.iso.date().optional().catch(undefined),
  ioStartDate: z.iso.date().optional().catch(undefined),
  ioEndDate: z.iso.date().optional().catch(undefined),
  diagCampaignOrInsertionId: z.string().optional().catch(undefined),
  diagCampaignOrInsertionName: z.string().optional().catch(undefined),
  adGroupPage: z.coerce.number().int().min(1).default(1).catch(1),
  adGroupPageSize: z.coerce
    .number()
    .int()
    .refine((val) => [5, 10, 25].includes(val))
    .default(10)
    .catch(10),
  adGroupSearch: z.string().optional().catch(undefined),
});

export type StarvationBidderTab = z.infer<
  typeof StarvationBidderSearchParamsSchema
>['starve'];

export type StarvationBidderSearchParams = Pick<
  z.infer<typeof StarvationBidderSearchParamsSchema>,
  | 'page'
  | 'pageSize'
  | 'ordering'
  | 'algorithmStatus'
  | 'performanceStatus'
  | 'campaignStartDate'
  | 'campaignEndDate'
>;

export type CampaignDiagnosticsSorting = z.infer<
  typeof StarvationBidderSearchParamsSchema
>['ordering'];

export type DiagnosticsTabs = z.infer<
  typeof StarvationBidderSearchParamsSchema
>['diag'];

export type EfficiencyInsightsSorting = z.infer<
  typeof StarvationBidderSearchParamsSchema
>['efficiencyInsightsOrdering'];
